import { z } from "zod";
import { isLabId, LAB_IDS, type LabId } from "./catalogue/families.ts";
import { baseModelNameOf, DEFAULT_BEDROCK_MODEL_IDS, MODEL_CATALOGUE } from "./catalogue/views.ts";
import { ROUTE_EQUIVALENT_EMBEDDINGS } from "./embedding.ts";
import type { ModelParameterSupport } from "./model-parameters.ts";

export * from "./catalogue/capabilities.ts";
export * from "./catalogue/families.ts";
export * from "./catalogue/lab-states.ts";
export * from "./catalogue/versions.ts";
// The catalogue's tables are views of its offerings and canonical models (ADR 0017 §1).
export {
  baseModelNameOf,
  DEFAULT_BEDROCK_MODEL_IDS,
  LIST_PRICES,
  MODEL_CATALOGUE,
  modelAcceptsImages,
  parameterSupport,
} from "./catalogue/views.ts";

/**
 * Callers ask for a model by *role*, never by provider model ID, so utility tasks
 * (titles, summaries…) can be rerouted when a model or region is degraded.
 */
export const MODEL_ROLES = ["chat", "reasoning", "fast", "vision"] as const;
export type ModelRole = (typeof MODEL_ROLES)[number];

/**
 * Bedrock inference-profile geographies (the profile ID's first segment: `eu.anthropic.…`).
 * Profiles are invoked in the deployment's Region, so only the EU geography and `global` (any
 * commercial Region) exist for an EU deployment.
 */
export const INFERENCE_GEOGRAPHIES = ["eu", "global"] as const;
export type InferenceGeography = (typeof INFERENCE_GEOGRAPHIES)[number];

/**
 * Data residency of model inference (`models.residency`): `eu` keeps every call on EU
 * profiles; `global` may also use global profiles, with the EU ones as later fallbacks.
 * Stored data stays in the deployment's Region either way.
 */
export const RESIDENCIES = ["eu", "global"] as const;
export const residencySchema = z.enum(RESIDENCIES);
export type Residency = z.infer<typeof residencySchema>;

/** Geographies a residency may use, in preference order. */
export function geographiesFor(residency: Residency): readonly InferenceGeography[] {
  return residency === "global" ? ["global", "eu"] : ["eu"];
}

export function isInferenceGeography(value: string): value is InferenceGeography {
  return (INFERENCE_GEOGRAPHIES as readonly string[]).includes(value);
}

/** Bedrock's cross-Region profile prefixes; ESI invokes only {@link INFERENCE_GEOGRAPHIES}. */
const PROFILE_PREFIXES: ReadonlySet<string> = new Set([
  ...INFERENCE_GEOGRAPHIES,
  "us",
  "us-gov",
  "apac",
  "jp",
  "au",
  "ca",
]);

/** `<geography>` for `<geography>.<provider>.<model>`; "" for a Region-local model ID. */
export function geographyOf(profileId: string): string {
  const dot = profileId.indexOf(".");
  const prefix = dot > 0 ? profileId.slice(0, dot) : "";
  return PROFILE_PREFIXES.has(prefix) ? prefix : "";
}

/**
 * The foundation model behind a profile: `<provider>.<model>` for `<geography>.<provider>.<model>`.
 * A foundation model ID (no geography prefix) is returned as is.
 */
export function foundationModelOf(profileId: string): string {
  const dot = profileId.indexOf(".");
  return dot > 0 && PROFILE_PREFIXES.has(profileId.slice(0, dot))
    ? profileId.slice(dot + 1)
    : profileId;
}

/**
 * The keys a route's price is looked up by, most specific first and de-duplicated: the route ID
 * (`eu.meta.llama-x`), its foundation ID (`meta.llama-x`) and the base model name without the
 * lab prefix (`llama-x`), for every lab alike (#94). `LIST_PRICES` holds all three kinds.
 */
export function priceKeysOf(routeId: string): string[] {
  const foundation = foundationModelOf(routeId);
  return [...new Set([routeId, foundation, baseModelNameOf(foundation)])];
}

/** Role → ordered candidate IDs per geography (the defaults, or a stage's `ESI_MODEL_ROLES`). */
export type RoleCandidateIds = Readonly<
  Record<InferenceGeography, Readonly<Record<ModelRole, readonly string[]>>>
>;

/**
 * The candidate IDs a deployment routes: {@link DEFAULT_BEDROCK_MODEL_IDS} with a stage's
 * per-geography, per-role overrides applied (the Bedrock adapter's merge).
 */
export function routedModelIds(
  overrides: Partial<
    Record<
      InferenceGeography,
      Partial<Record<ModelRole, readonly string[] | undefined>> | undefined
    >
  > = {},
): RoleCandidateIds {
  const ids = {
    eu: { ...DEFAULT_BEDROCK_MODEL_IDS.eu },
    global: { ...DEFAULT_BEDROCK_MODEL_IDS.global },
  };
  for (const geography of INFERENCE_GEOGRAPHIES) {
    for (const role of MODEL_ROLES) {
      const override = overrides[geography]?.[role];
      if (override) {
        ids[geography][role] = override;
      }
    }
  }
  return ids;
}

/**
 * Bedrock data retention modes a model may require (spec `model-policy`), least to most
 * permissive: AWS orders them `none < default < aws_review < provider_data_share`, and a model
 * is invocable when the account's effective mode is at or above the one it requires. ESI
 * declares only the two its catalogue needs; `default` is the fail-safe.
 */
export const RETENTION_MODES = ["default", "aws_review"] as const;
export const retentionModeSchema = z.enum(RETENTION_MODES);
export type RetentionMode = z.infer<typeof retentionModeSchema>;

/** True when an instance declaring `declared` may call a model requiring `required`. */
export function retentionSatisfies(declared: RetentionMode, required: RetentionMode): boolean {
  return RETENTION_MODES.indexOf(declared) >= RETENTION_MODES.indexOf(required);
}

/**
 * The stage's Bedrock retention mode (`ESI_MODEL_RETENTION_MODE`, from `modelRetentionMode`):
 * `default` when unset. Throws on an unknown mode, so a typo fails at startup. CDK cannot set
 * the account's mode: the stage declares what the operator set.
 */
export function retentionModeFromEnv(env: Record<string, string | undefined>): RetentionMode {
  const raw = env.ESI_MODEL_RETENTION_MODE?.trim();
  if (!raw) {
    return "default";
  }
  const parsed = retentionModeSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`ESI_MODEL_RETENTION_MODE: unknown retention mode "${raw}"`);
  }
  return parsed.data;
}

/** What using a model implies, stated per model (never for the whole account). */
export const modelImplicationsSchema = z.object({
  /** The Bedrock retention mode this model requires. */
  retention: retentionModeSchema,
  /** Geographies whose inference profiles route this model. */
  geographies: z.array(z.enum(INFERENCE_GEOGRAPHIES)).min(1),
  /** Bedrock: the model provider never receives requests or outputs. */
  providerReceivesData: z.boolean(),
  /** 1 (cheapest) to 4. */
  relativeCost: z.int().min(1).max(4),
  /** 1 (slowest) to 3. */
  relativeSpeed: z.int().min(1).max(3),
});
export type ModelImplications = z.infer<typeof modelImplicationsSchema>;

export interface CatalogueModel {
  readonly displayName: string;
  readonly provider: "amazon-bedrock";
  readonly implications: ModelImplications;
  /** Bumped whenever `implications` change: a consent is to one version. */
  readonly implicationsVersion: number;
  /**
   * True when `retention` is not `default` or the provider receives data: a tenant admin must
   * accept the implications.
   */
  readonly consentRequired: boolean;
  /** The model reasons natively in some style (`parameters.reasoning` is set). */
  readonly reasoning: boolean;
  /** Retired by hand (`offering-overrides.ts`): known for stored values, never a candidate. */
  readonly retired?: true;
  /** What the model supports (model-picking D15): reasoning style and levels, its limits. */
  readonly parameters: ModelParameterSupport;
}

/**
 * Bumped (a date, with a `.n` suffix for a second change the same day) on any catalogue change;
 * recorded with usage and on each answer.
 */
export const MODEL_CATALOGUE_VERSION = "2026-11-03";

/** A catalogue key: an offering's Bedrock foundation model ID (`catalogue/offerings.ts`). */
export type ModelKey = keyof typeof MODEL_CATALOGUE;
export const MODEL_KEYS = Object.keys(MODEL_CATALOGUE) as ModelKey[];

export function isModelKey(value: string): value is ModelKey {
  return Object.hasOwn(MODEL_CATALOGUE, value);
}

/** The catalogue entry behind a profile or model ID; undefined for an uncatalogued model. */
export function catalogueModelOf(
  modelId: string,
): { key: ModelKey; model: CatalogueModel } | undefined {
  const key = foundationModelOf(modelId);
  return isModelKey(key) ? { key, model: MODEL_CATALOGUE[key] } : undefined;
}

/**
 * The profile through which the pinned embedding model is called for `geography`: same
 * foundation model, so vectors stay comparable and nothing is re-indexed (ADR 0006). Under
 * `global`, only models in {@link ROUTE_EQUIVALENT_EMBEDDINGS} take the `global.` route.
 */
export function embeddingRoute(
  modelId: string,
  geography: InferenceGeography,
  equivalent: ReadonlySet<string> = ROUTE_EQUIVALENT_EMBEDDINGS,
): string {
  const foundation = foundationModelOf(modelId);
  if (foundation === modelId) {
    // A Region-local model ID (no profile): there is no route to choose.
    return modelId;
  }
  if (geography === "global" && !equivalent.has(foundation)) {
    return `eu.${foundation}`;
  }
  return `${geography}.${foundation}`;
}

/**
 * The labs the deployment is granted (`ESI_MODEL_LABS`, comma-separated, from the stage's
 * `modelLabs`; model-picking D16): IAM grants invoke per allowed lab and geography, so the
 * runtime settings (`models.providers`, `models.labs`) may only narrow within it. Every lab of
 * `MODEL_LABS` (each has a Bedrock prefix) when unset; throws on an unknown lab, so a typo fails
 * at startup instead of silently narrowing.
 */
export function modelLabsFromEnv(env: Record<string, string | undefined>): readonly LabId[] {
  const raw = env.ESI_MODEL_LABS?.trim();
  if (!raw) {
    return [...LAB_IDS];
  }
  const labs = new Set<LabId>();
  for (const value of raw.split(",").map((v) => v.trim())) {
    if (!isLabId(value)) {
      throw new Error(`ESI_MODEL_LABS: unknown lab "${value}"`);
    }
    labs.add(value);
  }
  return [...labs];
}

/**
 * The geographies the deployment is granted (`ESI_MODEL_GEOGRAPHIES`, comma-separated, from the
 * stage's `modelGeographies`): the ceiling of `models.residency`. EU only when unset. Throws on
 * an unknown geography, so a typo fails at startup instead of silently narrowing.
 */
export function modelGeographiesFromEnv(
  env: Record<string, string | undefined>,
): readonly InferenceGeography[] {
  const raw = env.ESI_MODEL_GEOGRAPHIES?.trim();
  if (!raw) {
    return ["eu"];
  }
  const geographies = new Set<InferenceGeography>(["eu"]);
  for (const value of raw.split(",").map((v) => v.trim())) {
    if (!isInferenceGeography(value)) {
      throw new Error(`ESI_MODEL_GEOGRAPHIES: unknown geography "${value}"`);
    }
    geographies.add(value);
  }
  return [...geographies];
}

/**
 * A resolved `models.residency` as shown to users: the value, the level it comes from, and
 * whether a level above (or the instance) locks it.
 */
export const residencyViewSchema = z.object({
  value: residencySchema,
  source: z.enum(["instance", "tenant", "entity", "project"]),
  locked: z.boolean(),
});
export type ResidencyView = z.infer<typeof residencyViewSchema>;
